import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Button, message, Tag, Space, Typography, Tooltip, Upload } from 'antd';
import ResponsiveTable from '../components/ResponsiveTable';
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, EyeOutlined, UploadOutlined, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { productsApi, dictionariesApi } from '../services/api-services';
const { TextArea } = Input;
const { Paragraph } = Typography;

const API_BASE = (process.env.REACT_APP_API_URL || '/api/v1').replace(/\/api\/v1$/, '');

function BlobPreview({ record, mediaType, height }: { record: any; mediaType: 'video' | 'image' | 'pdf'; height: number }) {
  const [src, setSrc] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const url = `${API_BASE}/api/v1/products/${record.id}/download`;

  useEffect(() => {
    let objectUrl = '';
    setDimensions(null);
    const token = localStorage.getItem('token');
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(''));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);

  if (src === null) return <div style={{ textAlign: 'center', padding: 32 }}>加载中...</div>;
  if (!src) return <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted-text)' }}>加载失败</div>;

  if (mediaType === 'image') {
    return (
      <div>
        <img
          src={src}
          alt={record.name}
          onLoad={(e) => setDimensions({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
          onError={() => setSrc('')}
          style={{ maxWidth: '100%', maxHeight: height - 32, display: 'block', margin: '0 auto', borderRadius: 8 }}
        />
        {dimensions && <div style={{ marginTop: 8, textAlign: 'center', color: 'var(--muted-text)', fontSize: 12 }}>原始尺寸：{dimensions.width} × {dimensions.height}</div>}
      </div>
    );
  }
  if (mediaType === 'pdf') {
    return <iframe src={src} title={record.name} style={{ width: '100%', height, border: 'none', borderRadius: 8 }} />;
  }
  return (
    <div>
      <video
        controls
        autoPlay
        onLoadedMetadata={(e) => setDimensions({ width: e.currentTarget.videoWidth, height: e.currentTarget.videoHeight })}
        style={{ width: '100%', maxHeight: height - 32, borderRadius: 8 }}
      >
        <source src={src} />
        您的浏览器不支持视频播放
      </video>
      {dimensions && <div style={{ marginTop: 8, textAlign: 'center', color: 'var(--muted-text)', fontSize: 12 }}>原始尺寸：{dimensions.width} × {dimensions.height}</div>}
    </div>
  );
}

function PreviewContent({ record, height }: { record: any; height: number }) {
  const url = `${API_BASE}/api/v1/products/${record.id}/download`;
  const type: string = record.product_type || '';

  if (type === 'image') return <BlobPreview record={record} mediaType="image" height={height} />;
  if (type === 'video') return <BlobPreview record={record} mediaType="video" height={height} />;
  if (type === 'webpage') {
    const src = record.file_path?.startsWith('http') ? record.file_path : url;
    return <iframe src={src} title={record.name} style={{ width: '100%', height, border: 'none', borderRadius: 8 }} />;
  }
  if (type === 'document') {
    return <DocumentPreview url={url} record={record} height={height} />;
  }
  // other / fallback
  return (
    <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted-text)' }}>
      <p>暂不支持预览此类型，请下载后查看。</p>
      <p style={{ fontSize: 12 }}>{record.file_path}</p>
    </div>
  );
}

function DocumentPreview({ url, record, height }: { url: string; record: any; height: number }) {
  const [text, setText] = useState<string | null>(null);
  const ext = (record.file_path || '').split('.').pop()?.toLowerCase();

  useEffect(() => {
    if (['md', 'txt', 'json', 'yaml', 'yml', 'csv', 'log', 'py', 'js', 'ts', 'html', 'css'].includes(ext)) {
      fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then(r => r.text()).then(setText).catch(() => setText('加载失败'));
    }
  }, [url, ext]);

  if (text !== null) {
    return (
      <pre style={{ height, overflow: 'auto', background: '#f5f5f5', padding: 16, borderRadius: 8, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        {text}
      </pre>
    );
  }
  // PDF or other binary doc
  if (ext === 'pdf') {
    return <BlobPreview record={record} mediaType="pdf" height={height} />;
  }
  return (
    <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted-text)' }}>
      <p>暂不支持在线预览此格式，请下载后查看。</p>
      <Paragraph copyable={{ text: record.file_path }} style={{ fontSize: 12 }}>{record.file_path}</Paragraph>
    </div>
  );
}

const ProductList: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [previewRecord, setPreviewRecord] = useState<any>(null);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [typeOptions, setTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [typeMap, setTypeMap] = useState<Record<string, string>>({});
  const [form] = Form.useForm();
  const [selecting, setSelecting] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [batchDeleteVisible, setBatchDeleteVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    dictionariesApi.getByCode('product_type').then((dict: any) => {
      const items: any[] = dict?.items || [];
      setTypeOptions(items.map((i: any) => ({ label: i.name, value: i.value })));
      setTypeMap(Object.fromEntries(items.map((i: any) => [i.value, i.name])));
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async (page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const params: any = { page, page_size: size };
      if (filterType) params.product_type = filterType;
      const res: any = await productsApi.list(params);
      setData(res.items);
      setTotal(res.total);
    } catch (e) {
      message.error('获取产物列表失败');
    }
    setLoading(false);
  }, [filterType, currentPage, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (values: any) => {
    try {
      if (editingItem) {
        const { file_path, ...updateValues } = values;
        await productsApi.update(editingItem.id, updateValues);
        message.success('更新成功');
      } else {
        await productsApi.create(values);
        message.success('创建成功');
      }
      setEditModalVisible(false);
      form.resetFields();
      setEditingItem(null);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '操作失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await productsApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (e) {
      message.error('删除失败');
    }
  };

  const handleBatchDelete = async () => {
    try {
      await Promise.all(selectedRowKeys.map(id => productsApi.delete(id)));
      message.success(`已删除 ${selectedRowKeys.length} 条`);
      setSelectedRowKeys([]);
      setSelecting(false);
      setBatchDeleteVisible(false);
      fetchData();
    } catch {
      message.error('批量删除失败');
    }
  };

  const handleDeleteBtnClick = () => {
    if (!selecting) {
      setSelecting(true);
      setSelectedRowKeys([]);
    } else if (selectedRowKeys.length > 0) {
      setBatchDeleteVisible(true);
    }
  };

  const handleDownload = async (record: any) => {
    if (!record.file_path) { message.warning('暂无文件路径'); return; }
    try {
      const blob: any = await productsApi.download(record.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = record.file_path.split('/').pop() || record.name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error('下载失败');
    }
  };

  const columns = [
    { title: '名称', dataIndex: 'name', key: 'name', render: (name: string, record: any) => record.description ? <Tooltip title={record.description}><span style={{ cursor: 'default', borderBottom: '1px dashed #aaa' }}>{name}</span></Tooltip> : name },
    {
      title: '类型', dataIndex: 'product_type', key: 'product_type', align: 'center' as const,
      render: (type: string) => <Tag>{typeMap[type] || type}</Tag>,
    },
    {
      title: '数字人', dataIndex: 'digital_employee_name', key: 'digital_employee_name', width: 130, align: 'center' as const,
      render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '-',
    },
    {
      title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 170,
      render: (v: string) => v ? new Date(v).toLocaleString() : '-',
    },
    {
      title: '操作', key: 'action', width: 200, fixed: 'right' as const, align: 'center' as const,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => { setPreviewRecord(record); setPreviewFullscreen(false); }}>预览</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditingItem(record); form.setFieldsValue(record); setEditModalVisible(true); }}>编辑</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">产物管理</h1>
      </div>
      <p className="apple-page-subtitle">浏览和管理各类产物文件，包括文档、图片、视频和网页。</p>
      <div className="apple-toolbar">
        <Select allowClear placeholder="按类型筛选" style={{ width: 150 }} value={filterType} onChange={(val) => setFilterType(val)} options={typeOptions} />
        <Space>
          {selecting && (
            <Button className="apple-btn-secondary" onClick={() => { setSelecting(false); setSelectedRowKeys([]); }}>取消选择</Button>
          )}
          <Button
            className="apple-btn-secondary"
            danger={selecting && selectedRowKeys.length > 0}
            icon={<DeleteOutlined />}
            onClick={handleDeleteBtnClick}
          >
            删除{selecting && selectedRowKeys.length > 0 ? `（${selectedRowKeys.length}）` : ''}
          </Button>
          <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); form.resetFields(); setEditModalVisible(true); }}>新建产物</Button>
        </Space>
      </div>

      <ResponsiveTable
        columns={columns} dataSource={data} rowKey="id" loading={loading}
        pagination={{ current: currentPage, pageSize: pageSize, total: total, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t) => `共 ${t} 条`, onChange: (page, size) => { setCurrentPage(page); setPageSize(size); fetchData(page, size); } }}
        rowSelection={selecting ? {
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as number[]),
        } : undefined}
      />

      <Modal
        title="确认删除"
        open={batchDeleteVisible}
        onOk={handleBatchDelete}
        onCancel={() => { setBatchDeleteVisible(false); setSelectedRowKeys([]); setSelecting(false); }}
        okText="删除"
        okButtonProps={{ danger: true }}
        cancelText="取消"
      >
        确定删除选中的 <strong>{selectedRowKeys.length}</strong> 条产物吗？此操作不可恢复。
      </Modal>

      {/* Preview Modal */}
      <Modal
        title={<Tooltip title={previewRecord?.file_path}><span style={{ display: 'block', paddingRight: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewRecord?.name}</span></Tooltip>}
        open={!!previewRecord}
        onCancel={() => { setPreviewRecord(null); setPreviewFullscreen(false); }}
        footer={[
          <Button key="close" onClick={() => { setPreviewRecord(null); setPreviewFullscreen(false); }}>关闭</Button>,
        ]}
        width={previewFullscreen ? '96vw' : 800}
        style={previewFullscreen ? { top: 16 } : undefined}
        bodyStyle={{ height: previewFullscreen ? 'calc(100vh - 160px)' : 540, overflow: 'auto' }}
        destroyOnClose
      >
        <Button
          type="text"
          size="small"
          title={previewFullscreen ? '还原' : '最大化'}
          icon={previewFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={() => setPreviewFullscreen(!previewFullscreen)}
          style={{ position: 'absolute', top: 17, right: 46, zIndex: 1 }}
        />
        {previewRecord && <PreviewContent record={previewRecord} height={previewFullscreen ? window.innerHeight - 200 : 500} />}
      </Modal>

      {/* Edit Modal */}
      <Modal
        className="apple-modal"
        title={editingItem ? '编辑产物' : '新建产物'}
        open={editModalVisible}
        onCancel={() => { setEditModalVisible(false); form.resetFields(); setEditingItem(null); }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form className="apple-form" form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="请输入产物名称" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>
          <Form.Item
            name="file_path"
            label="文件路径"
            rules={[{ required: true, message: '请选择上传文件' }]}
            getValueFromEvent={(e) => e?.file?.response?.file_path || e}
          >
            {editingItem ? (
              <Paragraph copyable={{ text: editingItem.file_path }} style={{ marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {editingItem.file_path}
              </Paragraph>
            ) : (
              <Upload
                maxCount={1}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    const formData = new FormData();
                    formData.append('file', file as File);
                    const res: any = await productsApi.uploadFile(formData);
                    form.setFieldValue('file_path', res.file_path);
                    message.success('文件上传成功');
                    onSuccess?.(res);
                  } catch (e: any) {
                    message.error(e.response?.data?.detail || '文件上传失败');
                    onError?.(e);
                  }
                }}
                onRemove={() => form.setFieldValue('file_path', undefined)}
              >
                <Button icon={<UploadOutlined />}>选择文件</Button>
              </Upload>
            )}
          </Form.Item>
          <Form.Item name="product_type" label="产物类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select placeholder="请选择产物类型" options={typeOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductList;
